import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Brain, TrendingUp, Clock, Award, Home, Download, Loader2, Sparkles, Target, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResultPage = ({ user }) => {
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    const fetchResult = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/tests/result`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setResult(response.data);
        setLoading(false);
      } catch (error) {
        toast.error('No test results found');
        navigate('/dashboard');
      }
    };

    fetchResult();
  }, [navigate]);

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tests/result/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PsychTest_Report_${user?.name?.replace(' ', '_') || 'Student'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download report');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Brain className="w-16 h-16 text-indigo-500 mx-auto mb-4 animate-pulse" />
          <p className="text-lg text-slate-600">Loading results...</p>
        </div>
      </div>
    );
  }

  const getPerformanceMessage = (percentage) => {
    if (percentage >= 80) return { message: 'Excellent!', color: 'text-teal-600', bg: 'bg-teal-50', desc: 'Outstanding performance! You have demonstrated strong cognitive abilities.' };
    if (percentage >= 60) return { message: 'Good Job!', color: 'text-indigo-600', bg: 'bg-indigo-50', desc: 'Good performance! You have shown solid cognitive abilities.' };
    if (percentage >= 40) return { message: 'Fair', color: 'text-amber-600', bg: 'bg-amber-50', desc: 'Average performance. Focus on improvement areas below.' };
    return { message: 'Keep Practicing', color: 'text-slate-600', bg: 'bg-slate-50', desc: 'Regular practice will help improve your cognitive skills.' };
  };

  const performance = getPerformanceMessage(result.percentage);

  // Calculate strengths and improvements
  const strengths = [];
  const improvements = [];
  Object.entries(result.category_scores).forEach(([category, scores]) => {
    const catPercentage = (scores.score / scores.max_score) * 100;
    if (catPercentage >= 70) strengths.push(category);
    else if (catPercentage < 50) improvements.push(category);
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Brain className="w-8 h-8 text-indigo-500" />
            <h1 className="font-heading text-2xl font-bold text-slate-900">PsychTest</h1>
            <div className="hidden sm:flex items-center gap-1 text-xs text-slate-500 border-l border-slate-200 pl-3">
              <Sparkles className="w-3 h-3" />
              <span>Powered by</span>
              <span className="font-semibold text-indigo-600">Uxima Foundation</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="download-pdf-btn"
              onClick={downloadPdf}
              disabled={downloadingPdf}
              className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-full px-4"
            >
              {downloadingPdf ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </>
              )}
            </Button>
            <Button
              data-testid="home-btn"
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="flex items-center gap-2"
            >
              <Home className="w-4 h-4" />
              Dashboard
            </Button>
          </div>
        </div>
      </nav>

      {/* Results Content */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Header Card */}
        <Card className={`${performance.bg} border-2 rounded-3xl shadow-xl mb-8`}>
          <CardContent className="p-8 text-center">
            <Award className={`w-20 h-20 ${performance.color} mx-auto mb-4`} />
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-2">
              {performance.message}
            </h1>
            <p className="text-lg text-slate-600 mb-6">{performance.desc}</p>
            <div className="inline-block">
              <div className={`text-6xl font-heading font-extrabold ${performance.color}`}>
                {result.percentage}%
              </div>
              <p className="text-slate-600 mt-2">
                {result.score} out of {result.max_score} points
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card data-testid="total-score-card" className="bg-white rounded-2xl border border-slate-100 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-6 h-6 text-indigo-500" />
                <h3 className="font-heading font-semibold text-slate-900">Total Score</h3>
              </div>
              <p className="text-3xl font-bold text-slate-900">
                {result.score}/{result.max_score}
              </p>
            </CardContent>
          </Card>

          <Card data-testid="time-taken-card" className="bg-white rounded-2xl border border-slate-100 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-2">
                <Clock className="w-6 h-6 text-teal-500" />
                <h3 className="font-heading font-semibold text-slate-900">Time Taken</h3>
              </div>
              <p className="text-3xl font-bold text-slate-900">
                {Math.floor(result.time_taken / 60)}:{(result.time_taken % 60).toString().padStart(2, '0')}
              </p>
            </CardContent>
          </Card>

          <Card data-testid="grade-card" className="bg-white rounded-2xl border border-slate-100 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-2">
                <Award className="w-6 h-6 text-amber-500" />
                <h3 className="font-heading font-semibold text-slate-900">Grade</h3>
              </div>
              <p className="text-3xl font-bold text-slate-900 capitalize">
                {result.grade === 'graduation' ? 'Graduation' : `Grade ${result.grade}`}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Category-wise Performance */}
        <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm mb-8">
          <CardHeader>
            <CardTitle className="font-heading text-2xl">Category-wise Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {Object.entries(result.category_scores).map(([category, scores]) => {
                const categoryPercentage = (scores.score / scores.max_score) * 100;
                const barColor = categoryPercentage >= 70 ? 'from-teal-500 to-emerald-500' : 
                                 categoryPercentage >= 50 ? 'from-indigo-500 to-blue-500' : 
                                 'from-amber-500 to-orange-500';
                return (
                  <div key={category} data-testid={`category-${category}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-slate-900 capitalize">{category} Reasoning</span>
                      <span className={`font-bold ${categoryPercentage >= 70 ? 'text-teal-600' : categoryPercentage >= 50 ? 'text-indigo-600' : 'text-amber-600'}`}>
                        {categoryPercentage.toFixed(0)}%
                      </span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-4">
                      <div
                        className={`bg-gradient-to-r ${barColor} h-4 rounded-full transition-all`}
                        style={{ width: `${categoryPercentage}%` }}
                      />
                    </div>
                    <p className="text-sm text-slate-500 mt-1">{scores.score.toFixed(1)} / {scores.max_score} points</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Strengths & Improvements */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Strengths */}
          <Card className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl border border-teal-200">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2 text-teal-800">
                <Target className="w-5 h-5" />
                Your Strengths
              </CardTitle>
            </CardHeader>
            <CardContent>
              {strengths.length > 0 ? (
                <ul className="space-y-2">
                  {strengths.map(cat => (
                    <li key={cat} className="flex items-center gap-2 text-teal-700">
                      <span className="w-2 h-2 bg-teal-500 rounded-full"></span>
                      <span className="capitalize font-medium">{cat} Reasoning</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-slate-600">Keep practicing to identify your strengths!</p>
              )}
              <p className="text-sm text-teal-600 mt-4">Build on these strengths in your academic and career choices.</p>
            </CardContent>
          </Card>

          {/* Areas for Improvement */}
          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-200">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2 text-amber-800">
                <Lightbulb className="w-5 h-5" />
                Focus Areas
              </CardTitle>
            </CardHeader>
            <CardContent>
              {improvements.length > 0 ? (
                <ul className="space-y-2">
                  {improvements.map(cat => (
                    <li key={cat} className="flex items-center gap-2 text-amber-700">
                      <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                      <span className="capitalize font-medium">{cat} Reasoning</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-slate-600">Great job! No major areas needing immediate focus.</p>
              )}
              <p className="text-sm text-amber-600 mt-4">Regular practice will help improve these areas.</p>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Recommendations */}
        <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm mb-8">
          <CardHeader>
            <CardTitle className="font-heading text-xl">Personalized Recommendations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-indigo-50 rounded-xl">
                <h4 className="font-semibold text-indigo-800 mb-2">Logical Reasoning</h4>
                <p className="text-sm text-slate-600">Practice puzzles, sudoku, pattern recognition, and logical sequences to strengthen this area.</p>
              </div>
              <div className="p-4 bg-teal-50 rounded-xl">
                <h4 className="font-semibold text-teal-800 mb-2">Numerical Ability</h4>
                <p className="text-sm text-slate-600">Work on mental math, percentages, ratios, and data interpretation exercises daily.</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-xl">
                <h4 className="font-semibold text-purple-800 mb-2">Verbal Skills</h4>
                <p className="text-sm text-slate-600">Read regularly, expand vocabulary, and practice comprehension and analogy questions.</p>
              </div>
              <div className="p-4 bg-amber-50 rounded-xl">
                <h4 className="font-semibold text-amber-800 mb-2">Spatial Reasoning</h4>
                <p className="text-sm text-slate-600">Engage with 3D puzzles, maps, visual patterns, and geometric exercises.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Download CTA */}
        <div className="text-center">
          <Button
            data-testid="download-full-report-btn"
            onClick={downloadPdf}
            disabled={downloadingPdf}
            className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white rounded-full px-8 py-4 text-lg shadow-lg"
          >
            {downloadingPdf ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Generating PDF...
              </>
            ) : (
              <>
                <Download className="w-5 h-5 mr-2" />
                Download Full Report (PDF)
              </>
            )}
          </Button>
          <p className="text-sm text-slate-500 mt-3">Save your detailed report for future reference</p>
        </div>
      </div>
    </div>
  );
};

export default ResultPage;
